import RcDomainMap from "../../../common/RcDomainMap.js";
import VehicleData from "./VehicleData.js";

export default class VehicleDataMap extends RcDomainMap {

  static MODEL_NAME = "VehicleDataMap";

  constructor(domain, data) {
    super(domain, data, VehicleDataMap.MODEL_NAME);
  }

  getVehicleData(key) {
    var value = super.get(key);
    return new VehicleData(this.domain, value);
  }

  // The rows this map points at, resolved against the store.
  //
  // A row can arrive two ways: as a bare pointer, which only the store can fill
  // in, or spelled out in full because it travelled with the asset it belongs
  // to. A row that came in spelled out is kept when it is more recent than the
  // copy the store holds, because the store may be behind -- a client caches
  // these rows when it downloads its assets, and that cache is only as fresh as
  // the last download. Re-reading every row by id threw away the reading the
  // server had just sent and handed back the cached one, so a driver's odometer
  // stayed on the figure it had at sign-in however many inspections they filled
  // in.
  //
  // Readings are the server's record, so a row a client sends never wins there:
  // the handlers put the stored readings back on a submitted asset first.
  find() {
    var keys = this.keys();
    var found = new VehicleDataMap(this.domain, {});
    for (var index = 0; index < keys.length; index++) {
      var key = keys[index];
      var item = this.getVehicleData(key);
      if (item.isNew()) {
        found.add(item);
        continue;
      }
      var stored = new VehicleData(this.domain, this.domain.vehicles().vehicleData().findById(key));
      if (item.hasValue()
          && (stored.isNew() || !stored.hasValue() || item.recordedOn() > stored.recordedOn())) {
        found.add(item);
        continue;
      }
      if (!stored.isNew()) {
        found.add(stored);
      }
    }
    return found;
  }

  populate() {
    var found = this.find();
    this.addAll(found);
    return this;
  }

  list() {
    var values = [];
    var keys = this.keys();
    for (var index = 0; index < keys.length; index++) {
      values.push(this.getVehicleData(keys[index]));
    }
    return values;
  }

  // The row holding the latest value for what was measured, or a new empty row
  // when the asset has nothing recorded for it yet. An asset should only ever
  // hold one row per key, but the newest wins if duplicates ever appear.
  forKey(key) {
    // Whether a row has been chosen yet is tracked on its own rather than through
    // isNew(): that answers true for any row which has not been saved, so it stayed
    // true all the way round the loop and simply kept the last row seen instead of
    // the most recently recorded one.
    var found = null;
    var list = this.list();
    for (var index = 0; index < list.length; index++) {
      var item = list[index];
      if (!item.isKey(key)) {
        continue;
      }
      if (found === null || item.recordedOn() > found.recordedOn()) {
        found = item;
      }
    }
    if (found === null) {
      return new VehicleData(this.domain);
    }
    return found;
  }

  // Whether a value has been recorded for what was measured. Tests the value
  // rather than whether the row has been persisted, so a row built in memory and
  // not yet saved still counts.
  hasKey(key) {
    return this.forKey(key).hasValue();
  }

  odometer() {
    return this.forKey(VehicleData.Values.Keys.Odometer);
  }

  hours() {
    return this.forKey(VehicleData.Values.Keys.Hours);
  }

  copy() {
    var data = this._copy();
    return new VehicleDataMap(this.domain, data);
  }
}
